import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  storyboardCueAt,
  storyboardPreviewContain,
  storyboardSpanMs,
  type StoryboardCue,
  type StoryboardPreview,
  type VisiousSettings,
} from '@visious/core';
import { fetchGridStoryboardCues } from '../api/storyboard';
import { formatDuration } from '../format';
import { getPlatform } from '../platform/instance';
import { readSettings, useSettingsSlice } from '../platform/settings';

/** Module-level so the branch is read the same way on every call — `useSettingsSlice` says why. */
const selectGridPreviews = (settings: VisiousSettings): boolean => settings.gridPreviews;

/**
 * The grid hover preview (2026-08-02): rest the pointer on a card's thumbnail and it becomes
 * a flipbook of the video's storyboard tiles, the bottom bar following the flipped-to position —
 * and moving the pointer along the bar's strip scrubs the flipbook the way seeking scrubs the
 * player. All of it is decoration: the card stays the same link, a keyboard never sees it, and a
 * track that never arrives (a live stream, a 404) leaves the thumbnail exactly as it was.
 *
 * Three clocks, each with a reason:
 * - **250 ms before the track is fetched** — sweeping the pointer across a grid crosses a dozen
 *   cards, and a dozen ~27 KB VTT fetches per sweep is the failure this timer exists to prevent.
 * - **1000 ms before the preview shows** ("hovering … for a few seconds") — resting on a card is a
 *   statement, passing over it is not.
 * - **1/40 of the video per 300 ms tick** (2026-08-02) — by percentage, not by tile: one
 *   tile per tick swept a 10-minute video in 3 minutes, which on anything long meant the preview
 *   showed the opening seconds for as long as anyone hovered. A fixed fraction makes every video
 *   sweep end-to-end in the same 12 s, whatever its length.
 */
const PREFETCH_DELAY_MS = 250;
const PREVIEW_DELAY_MS = 1000;
const ADVANCE_MS = 300;
const STEP_FRACTION = 1 / 40;

/** The scrub strip: the bottom edge of the box, tall enough to hit without aiming at a 3 px bar. */
const SCRUB_ZONE_PX = 24;

/**
 * Which sheet images this session has decoded — module-level, so a second hover on the same card
 * (or on a card the reader already passed) starts instantly, and so two cards of the same video
 * never fetch the same sheet twice.
 *
 * **A track is many images, and that is what caused the black flashes** (2026-08-02):
 * `aqz-KE-bpKQ` is 135 cues across **15** sheets — 9 cues to a sheet — while the flipbook steps
 * 1/40 of the video, about 3.4 cues, so it crosses a sheet boundary every third tick or so. Painting
 * a `background-image` the browser has not fetched yet shows the tile box's own black until it
 * lands. So nothing is painted until its sheet is decoded, and each tick warms the *next* one.
 *
 * The set is also **read during render** (`painted` below), which is what makes a re-hover paint on
 * its first frame instead of one frame later: a decode can only report through a promise, and any
 * paint between "showing" and "decoded" is the loading note flashing on a preview that was already
 * there. A cache read is safe there because nothing waits on the set changing — a decode that lands
 * later sets state, and that is what re-renders the card.
 */
const decodedSheets = new Set<string>();
const decoding = new Map<string, Promise<boolean>>();
const RESOLVED_TRUE = Promise.resolve(true);

function decodeSheet(url: string): Promise<boolean> {
  if (decodedSheets.has(url)) return RESOLVED_TRUE;
  const already = decoding.get(url);
  if (already !== undefined) return already;

  const attempt = new Promise<boolean>((resolve) => {
    const image = new Image();
    image.onload = () => {
      decodedSheets.add(url);
      decoding.delete(url);
      resolve(true);
    };
    image.onerror = () => {
      // Not cached as a failure: the `/sb/` proxy is intermittent the way `/vi/` is (`Thumbnail`'s
      // one retry), and the next tick asking again is the whole of the retry this needs.
      decoding.delete(url);
      resolve(false);
    };
    image.src = url;
  });
  decoding.set(url, attempt);
  return attempt;
}

/** What the corner of the frame is saying while the preview is not yet a picture. */
export type GridPreviewStatus = 'loading' | 'failed';

export interface GridPreviewBinding {
  /** Spread onto the thumbnail box — `Thumbnail` passes them through to its root span. */
  readonly onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerLeave: () => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  /**
   * The crop to draw over the image, or `null` while the thumbnail should stand. Contain-fit
   * (2026-08-02): a vertical Short's tile is taller than the 16:9 box, so the crop is scaled
   * to fit *inside* and `leftPx`/`topPx` centre it — the box's own black is the letterbox.
   */
  readonly tile: {
    readonly crop: StoryboardPreview;
    readonly leftPx: number;
    readonly topPx: number;
  } | null;
  /**
   * The synced bar while the preview shows — replaces the card's watched-progress bar. `exact`
   * because this one is a *position*, not a stored fraction: the 8 % floor that keeps two minutes
   * of a two-hour video visible would pin the preview bar's first three seconds to 8 % and make the
   * flipbook and the bar disagree (2026-08-02).
   */
  readonly progress: {
    readonly fraction: number;
    readonly title: string;
    readonly exact: true;
  } | null;
  /**
   * The corner note while the preview is coming or will not come (2026-08-02) — `null` once
   * there is a picture, and `null` on a row that never arms at all (a live stream, a premiere,
   * previews switched off), where there is nothing to announce. An **empty** track says "No preview"
   * rather than nothing: a hover that produces neither a picture nor a word reads as the
   * preview being broken, and "there is none" is the one thing the corner can usefully say.
   */
  readonly status: GridPreviewStatus | null;
}

export function useGridPreview(input: {
  readonly videoId: string;
  /** The record's `lengthSeconds` where known — the clamp against the track's blank tail. */
  readonly durationSec: number | null;
  /**
   * A row that cannot have a storyboard — a live stream, a premiere — so it never arms, not even
   * the fetch. Without it those rows would spend a hover fetching a 404 and then say "No preview"
   * about a video that has not been recorded yet.
   */
  readonly noPreview: boolean;
}): GridPreviewBinding {
  const { videoId, durationSec, noPreview } = input;
  // One switch rather than the document — `useDeArrowBranding` says why, and this is per card too.
  const enabled = useSettingsSlice(selectGridPreviews) && !noPreview;

  const [held, setHeld] = useState(false);
  const [showing, setShowing] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  /** The last cue whose sheet is decoded — what is on screen, held while the next one loads. */
  const [drawn, setDrawn] = useState<StoryboardCue | null>(null);
  // State, not a ref: the tile's scale is computed during render, where a ref may not be read.
  // Re-setting the same box is guarded below — an unconditional fresh object per pointer move
  // would defeat React's `Object.is` bailout and re-render the card at pointer speed.
  const [box, setBox] = useState({ width: 0, height: 0 });
  const timersRef = useRef<{ prefetch: number | null; show: number | null }>({
    prefetch: null,
    show: null,
  });

  const query = useQuery({
    queryKey: ['grid-storyboard', videoId],
    enabled: enabled && held,
    retry: false,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const boot = await getPlatform();
      // Thrown rather than answered with an empty track, so "the track did not arrive" and "this
      // video has no track" stay different states — the corner note says the same words for both,
      // but only one of them is worth asking the instance about again (R9: the message is a
      // marker, never a rendered string).
      if (!boot.ok) throw new Error('grid-storyboard: no platform');
      const result = await fetchGridStoryboardCues(boot.platform, videoId, durationSec);
      if (!result.ok) throw new Error('grid-storyboard: refused');
      /*
       * **An empty track stays data on purpose.** A `200` with no cues is a settled fact about the
       * video — this instance answers some of them with an empty body, the same intermittent nothing
       * that makes a payload arrive with no formats, and it is also what a `200 application/json`
       * parses to when the size parameters are dropped (`api/storyboard.ts`). Kept as data it is
       * cached for `staleTime` and costs one request per session; thrown, react-query would re-ask
       * on every single hover. What it must *not* do is stay silent — see `previewStatus`.
       */
      return [...result.value];
    },
  });
  const cues = useMemo(() => query.data ?? [], [query.data]);
  const spanMs = storyboardSpanMs(cues);

  const clearTimers = () => {
    const timers = timersRef.current;
    if (timers.prefetch !== null) window.clearTimeout(timers.prefetch);
    if (timers.show !== null) window.clearTimeout(timers.show);
    timers.prefetch = null;
    timers.show = null;
  };

  /** `Object.is`-friendly: only a genuinely different rectangle produces a new state object. */
  const measure = (element: HTMLElement): void => {
    const rect = element.getBoundingClientRect();
    setBox((current) =>
      current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height },
    );
  };

  const onPointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // A touch "enter" is a tap already navigating — arming a preview under it would flash a
      // frame nobody asked for on the way out. `readSettings` rather than the subscription value:
      // the handler is stable across renders, and the box is the freshest source either way.
      if (event.pointerType === 'touch' || !readSettings().gridPreviews || noPreview) return;
      measure(event.currentTarget);
      clearTimers();
      timersRef.current.prefetch = window.setTimeout(() => setHeld(true), PREFETCH_DELAY_MS);
      timersRef.current.show = window.setTimeout(() => setShowing(true), PREVIEW_DELAY_MS);
    },
    [noPreview],
  );

  const onPointerLeave = useCallback(() => {
    clearTimers();
    setHeld(false);
    setShowing(false);
    setScrubbing(false);
    setPositionMs(0);
    setDrawn(null);
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!showing || spanMs <= 0) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width <= 0) return;
      measure(event.currentTarget);
      // Only the bottom strip scrubs — the rest of the box is the flipbook minding its own clock.
      if (event.clientY < rect.bottom - SCRUB_ZONE_PX) {
        setScrubbing(false);
        return;
      }
      setScrubbing(true);
      const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      setPositionMs(fraction * spanMs);
    },
    [showing, spanMs],
  );

  // The flipbook's own clock — paused while the pointer is scrubbing the strip, and resuming from
  // wherever the scrub left the position rather than starting over. Steps are a fraction of the
  // video (`STEP_FRACTION`), not one cue: cue-sized steps swept long videos too slowly to ever
  // leave the opening minutes.
  useEffect(() => {
    if (!showing || scrubbing || cues.length === 0 || spanMs <= 0) return;
    const interval = window.setInterval(() => {
      setPositionMs((current) => {
        const next = current + spanMs * STEP_FRACTION;
        return next >= spanMs ? 0 : next;
      });
    }, ADVANCE_MS);
    return () => window.clearInterval(interval);
  }, [showing, scrubbing, cues, spanMs]);

  const active = enabled && showing && cues.length > 0 && spanMs > 0;

  /*
   * What to paint, and what to fetch next. The position moves ahead of the picture on purpose: a
   * cue whose sheet is not decoded yet leaves the previous frame up (or the thumbnail, at the
   * start) rather than flashing the box's black, and the step after this one is warmed while the
   * current one is being looked at — 300 ms is a comfortable head start for one ~40 KB sheet.
   */
  const cue = active ? storyboardCueAt(cues, positionMs) : null;
  /**
   * What to draw. The cue for this position when its sheet is **already decoded** — asked of the
   * module cache during render, which is what stops the corner note flashing on a card whose preview
   * was ready before the pointer arrived: `drawn` can only be set from a promise, so even an
   * instantly-resolved decode leaves one paint where there is no picture yet, and one paint is a
   * flicker. Otherwise the last decoded frame stands, or the thumbnail does.
   */
  const painted = cue !== null && decodedSheets.has(cue.imageUrl) ? cue : drawn;

  useEffect(() => {
    if (!active || cue === null) return;

    let current = true;
    void decodeSheet(cue.imageUrl).then((ok) => {
      // Same cue object for every position inside it, so React's bailout keeps this from
      // re-rendering the card on each pointer move along the scrub strip.
      if (ok && current) setDrawn(cue);
    });

    const aheadMs = (positionMs + spanMs * STEP_FRACTION) % spanMs;
    const ahead = storyboardCueAt(cues, aheadMs);
    if (ahead !== null && ahead.imageUrl !== cue.imageUrl) void decodeSheet(ahead.imageUrl);

    return () => {
      current = false;
    };
  }, [active, cue, cues, positionMs, spanMs]);

  const crop =
    active && painted !== null ? storyboardPreviewContain(painted, box.width, box.height) : null;
  const tile =
    crop === null
      ? null
      : {
          crop,
          // Centred on both axes — for a 16:9 tile this is a no-op, for a Short it is the framing.
          leftPx: Math.round((box.width - crop.boxWidth) / 2),
          topPx: Math.round((box.height - crop.boxHeight) / 2),
        };

  return {
    onPointerEnter,
    onPointerLeave,
    onPointerMove,
    tile,
    progress:
      tile === null
        ? null
        : { fraction: positionMs / spanMs, title: formatDuration(positionMs / 1000), exact: true },
    status: previewStatus({
      enabled,
      showing,
      failed: query.isError,
      fetching: query.isFetching,
      // An answer arrived. With no cues in it, that answer *is* "there is no preview".
      answered: query.isSuccess,
      hasTrack: cues.length > 0 && spanMs > 0,
      painted: tile !== null,
    }),
  };
}

/**
 * The corner note's one decision, kept out of the hook body because it is the part with the
 * branches: it says "coming" while there is a reason to expect a picture, "failed" when the track
 * was refused **or answered empty** (the two are one thing to a reader, and a hover that
 * produces neither a picture nor a word reads as the preview being broken), and nothing at all once a
 * tile is up or on a row that never armed.
 */
function previewStatus(input: {
  readonly enabled: boolean;
  readonly showing: boolean;
  readonly failed: boolean;
  readonly fetching: boolean;
  readonly answered: boolean;
  readonly hasTrack: boolean;
  readonly painted: boolean;
}): GridPreviewStatus | null {
  if (!input.enabled || !input.showing || input.painted) return null;
  if (input.failed) return 'failed';
  // The track still on the wire, or here and its first sheet still decoding — both are "coming".
  if (input.fetching || input.hasTrack) return 'loading';
  // An answer with nothing in it. Not silence: this is as final as a refusal.
  return input.answered ? 'failed' : null;
}
